// Home — ONE scrollable command-center page (no tabs)
import { ReactNode } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { Kpi, Delta, hoverLift } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { Donut } from "@/components/charts";
import { DATA, NavFn } from "@/data";

const TONE_C: Record<string, string> = { pos: "var(--pos)", warn: "var(--warn)", neg: "var(--neg)", info: "var(--info)" };

const SevDot = ({ sev }: { sev: string }) => {
  const c = ({ high: "var(--neg)", med: "var(--warn)", low: "var(--info)" } as Record<string, string>)[sev];
  return <span style={{ width: 8, height: 8, borderRadius: 50, background: c, flexShrink: 0, marginTop: 6 }} />;
};

const QUICK = [
  { l: "Ask AI", icon: "spark", accent: true },
  { l: "Generate Board Report", icon: "doc" },
  { l: "Open Cash & Forecast", icon: "wallet" },
  { l: "Review Transactions", icon: "list" },
  { l: "Create Invoice", icon: "receipt" },
  { l: "Upload Document", icon: "download" },
];

const SecLabel = ({ title, sub, right }: { title: ReactNode; sub?: string; right?: ReactNode }) => (
  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, marginBottom: 12 }}>
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em" }}>{title}</div>
      {sub && <div style={{ fontSize: 12.5, color: "var(--ink-4)", marginTop: 2 }}>{sub}</div>}
    </div>
    {right && <div style={{ flexShrink: 0 }}>{right}</div>}
  </div>
);

export const HomePage = ({ onNav }: { onNav: NavFn }) => {
  const notify = useNotImplemented();
  const d = DATA;
  const quickNav = (l: string) => {
    posthog.capture("home_quick_action_clicked", { action: l });
    if (l === "Ask AI") onNav("ai", "chat");
    else if (l === "Generate Board Report") onNav("finance", "reports");
    else if (l === "Open Cash & Forecast") onNav("finance", "cash");
    else onNav("finance", "transactions");
  };
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", flexDirection: "column", gap: 26 }} className="fade-up">

      {/* ── SECTION 1 · DAILY BRIEFING (HERO) ─────────────────────────── */}
      <section>
        <div className="card ai-surface" style={{ padding: "26px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
            <span className="ai-mark"><Icon name="spark" size={14} stroke="#fff" /></span>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Daily Briefing</span>
            <span className="badge b-brand">AI</span>
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span className="live-dot" style={{ width: 6, height: 6, borderRadius: 50, background: "var(--pos)" }} /> Updated 6 min ago
            </span>
          </div>
          <div style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-.02em", marginBottom: 12 }}>Good morning, Sarah 👋</div>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--ink-2)", margin: 0, maxWidth: 760, textWrap: "pretty" as const }}>
            Your business is <b style={{ color: "var(--ink)" }}>healthy</b> today. Cash is strong with <b style={{ color: "var(--ink)" }}>14.2 months</b> of
            runway and revenue is up <b style={{ color: "var(--ink)" }}>14%</b> this month. Collections slowed over the past week — I recommend
            chasing <b style={{ color: "var(--ink)" }}>three overdue invoices</b> worth $24,310 before they age further.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 20, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-pri" onClick={() => { posthog.capture("home_briefing_ask_ai_clicked"); onNav("ai", "chat"); }}><Icon name="spark" size={15} stroke="#fff" /> Ask AI</button>
              <button className="btn btn-sec" onClick={() => { posthog.capture("home_briefing_view_summary_clicked"); notify("View full summary"); }}>View full summary</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 11, marginLeft: "auto" }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Business Health</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--pos)" }}>Strong</div>
              </div>
              <Donut size={58} thickness={8}
                data={[{ value: 82, color: "var(--pos)" }, { value: 18, color: "rgba(22, 163, 74,.16)" }]}
                center={<div className="num" style={{ fontSize: 16, fontWeight: 700 }}>82</div>} />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2 · BUSINESS HEALTH ───────────────────────────────── */}
      <section>
        <SecLabel title="Business Health" sub="Six signals, plain-language status — no charts." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {d.health.map((h, i) => (
            <div key={i} className="card" style={{ padding: "15px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                <span style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 500 }}>{h.l}</span>
                <Delta v={h.delta} invert={(h as { invert?: boolean }).invert} size={11} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ width: 8, height: 8, borderRadius: 50, background: TONE_C[h.tone], flexShrink: 0 }} />
                <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em" }}>{h.status}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 5 }}>{h.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 3 · PRIORITY FEED ⭐ ───────────────────────────────── */}
      <section>
        <SecLabel
          title={<span>Priority Feed <span style={{ color: "var(--warn)" }}>★</span></span>}
          sub="AI recommendations ordered by priority. Each opens the relevant Finance page, filters applied."
          right={<span className="chip">{d.priority.length} items</span>} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {d.priority.map((p) => (
            <div key={p.id} className="card" style={{ padding: "16px 18px", display: "flex", gap: 14, alignItems: "flex-start", cursor: "pointer", transition: "box-shadow .15s,border-color .15s" }}
              onClick={() => { posthog.capture("home_priority_item_opened", { title: p.title, severity: p.sev, via: "card" }); onNav(p.nav[0], p.nav[1]); }} {...hoverLift}>
              <SevDot sev={p.sev} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  <span className={"badge " + (p.sev === "high" ? "b-neg" : p.sev === "med" ? "b-warn" : "b-info")} style={{ textTransform: "uppercase", letterSpacing: ".03em" }}>
                    {p.sev === "high" ? "High" : p.sev === "med" ? "Medium" : "Low"}
                  </span>
                  <span style={{ fontSize: 14.5, fontWeight: 600 }}>{p.title}</span>
                  <span className={"badge " + ((p as { good?: boolean }).good ? "b-pos" : "b-mute")} style={{ marginLeft: "auto" }}>{p.impact}</span>
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.55, maxWidth: 720 }}>{p.body}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 11 }}>
                  <button className="btn btn-sec btn-sm" onClick={(e) => { e.stopPropagation(); posthog.capture("home_priority_item_opened", { title: p.title, severity: p.sev, via: "action_button", action: p.action }); onNav(p.nav[0], p.nav[1]); }}>{p.action}</button>
                  <span style={{ fontSize: 11.5, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Icon name="link" size={13} stroke="var(--ink-4)" /> {p.link}
                  </span>
                  <span style={{ fontSize: 11.5, color: "var(--ink-4)", marginLeft: "auto" }}>{p.conf}% confidence</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 3.5 · PENDING APPROVALS — human-in-the-loop queue ─── */}
      {(() => {
        const pending = d.approvals.filter((a) => a.status === "pending");
        return (
          <section>
            <SecLabel title="Waiting for your approval" sub="Agent actions that crossed an approval rule. Everything else runs autonomously."
              right={<span className="link" style={{ fontSize: 12.5, cursor: "pointer" }} onClick={() => { posthog.capture("home_approvals_opened", { via: "header_link" }); onNav("aiagents", "approvals"); }}>Open approvals queue →</span>} />
            <div className="card">
              {pending.slice(0, 3).map((a, i, arr) => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: i < arr.length - 1 ? "1px solid var(--line-2)" : "none", cursor: "pointer" }}
                  onClick={() => { posthog.capture("home_approvals_opened", { via: "row", title: a.title, agent: a.agent }); onNav("aiagents", "approvals"); }}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, background: a.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={a.icon} size={16} stroke={a.color} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                    <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 1 }}>{a.agent} · {a.time}</div>
                  </div>
                  <span className="badge b-brand" style={{ flexShrink: 0 }}>{a.impact}</span>
                  <button className="btn btn-sec btn-sm" style={{ flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); posthog.capture("home_approvals_opened", { via: "review_button", title: a.title, agent: a.agent }); onNav("aiagents", "approvals"); }}>Review</button>
                </div>
              ))}
              {pending.length > 3 && (
                <div style={{ padding: "10px 18px", borderTop: "1px solid var(--line-2)", fontSize: 12, color: "var(--ink-4)", textAlign: "center", cursor: "pointer" }} onClick={() => { posthog.capture("home_approvals_opened", { via: "more_link" }); onNav("aiagents", "approvals"); }}>
                  +{pending.length - 3} more in the queue
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* ── SECTION 4 · EXECUTIVE SNAPSHOT (exactly 4) ────────────────── */}
      <section>
        <SecLabel title="Executive Snapshot" sub="The only KPIs on Home. Tap any card to open Finance → KPIs." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
          {d.snapshot.map((k) => (
            <Kpi key={k.key} label={k.label} value={k.value} delta={k.delta} invert={(k as { invert?: boolean }).invert}
              spark={k.spark} sparkColor={k.color} onClick={() => { posthog.capture("home_kpi_card_clicked", { kpi: k.label }); onNav("finance", "kpis"); }} />
          ))}
        </div>
      </section>

      {/* ── SECTION 5 · QUICK ACTIONS ─────────────────────────────────── */}
      <section>
        <SecLabel title="Quick Actions" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12 }}>
          {QUICK.map((q, i) => (
            <button key={i} onClick={() => quickNav(q.l)} className="card" style={{
              padding: "16px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 9, cursor: "pointer",
              border: q.accent ? "1px solid var(--brand-line)" : "1px solid var(--line)",
              background: q.accent ? "var(--brand-tint)" : "var(--surface)", transition: "box-shadow .15s,border-color .15s",
            }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
              <span style={{ width: 38, height: 38, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", background: q.accent ? "var(--brand)" : "var(--surface-3)" }}>
                <Icon name={q.icon} size={19} stroke={q.accent ? "#fff" : "var(--ink-2)"} />
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, textAlign: "center", lineHeight: 1.3, color: "var(--ink-2)" }}>{q.l}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── SECTION 6 · RECENT AI ACTIVITY ────────────────────────────── */}
      <section>
        <SecLabel title="Recent AI Activity" sub="What your agents did autonomously — plain language, no logs."
          right={<span className="link" style={{ fontSize: 12.5, cursor: "pointer" }} onClick={() => { posthog.capture("home_ai_timeline_opened"); onNav("aiagents", "dashboard"); }}>Full timeline →</span>} />
        <div className="card">
          {d.recentAiActivity.map((r, i, arr) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: i < arr.length - 1 ? "1px solid var(--line-2)" : "none" }}>
              <span style={{ width: 8, height: 8, borderRadius: 50, background: r.c, flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}><b style={{ color: "var(--ink)" }}>{r.a}</b> {r.t}</div>
              <span style={{ fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>{r.time} ago</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
